import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useAdminAPI } from '../hooks/useAdminAPI';
import { CulturalEvent } from '../constants/culturalEventsTypes';
import { LoadingSpinner } from '../Shared/LoadingSpinner';
import './AdminDashboard.css';

type SortDirection = 'asc' | 'desc';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAdminAuth();
  const { getAllEvents, publishEvent, unpublishEvent, deleteEvent, loading } = useAdminAPI();
  const [events, setEvents] = useState<CulturalEvent[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEvents = async () => {
    try {
      const data = await getAllEvents();
      // Handle both array and object with events property
      let eventsArray: CulturalEvent[] = [];
      if (Array.isArray(data)) {
        eventsArray = data;
      } else if (data && data.events && Array.isArray(data.events)) {
        eventsArray = data.events;
      } else {
        eventsArray = [];
      }
      
      // Debug: Log dates to verify sorting
      console.log('📅 Events loaded with dates:', eventsArray.map(e => ({
        title: e.title?.substring(0, 30),
        date: e.date,
        parsed: new Date(e.date).toISOString()
      })));
      
      setEvents(eventsArray);
    } catch (error) {
      console.error('Failed to load events');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/cultural-events/login');
  };

  const handlePublish = async (id: string) => {
    if (window.confirm('Are you sure you want to publish this event?')) {
      try {
        await publishEvent(id);
        loadEvents(); // Refresh list
      } catch (error) {
        alert('Failed to publish event');
      }
    }
  };

  const handleUnpublish = async (id: string) => {
    if (window.confirm('Are you sure you want to unpublish this event?')) {
      try {
        await unpublishEvent(id);
        loadEvents(); // Refresh list
      } catch (error) {
        alert('Failed to unpublish event');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      try {
        await deleteEvent(id);
        loadEvents(); // Refresh list
      } catch (error) {
        alert('Failed to delete event');
      }
    }
  };

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const stats = useMemo(() => ({
    total: events.length,
    published: events.filter(e => e.status === 'published').length,
    drafts: events.filter(e => e.status === 'draft').length,
    pending: events.filter(e => e.status === 'pending_review').length
  }), [events]);

  const filteredEvents = useMemo(() => {
    const filtered = events.filter(event => {
      if (filter === 'all') return true;
      return event.status === filter;
    });
    
    // Create a copy to avoid mutating the original array
    const sorted = [...filtered].sort((a, b) => {
      // Handle missing dates
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1; // Missing dates go to end
      if (!b.date) return -1; // Missing dates go to end
      
      // Parse dates and get timestamps
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      const timeA = dateA.getTime();
      const timeB = dateB.getTime();
      
      // Handle invalid dates (NaN)
      if (isNaN(timeA) && isNaN(timeB)) return 0;
      if (isNaN(timeA)) return 1; // Invalid dates go to end
      if (isNaN(timeB)) return -1; // Invalid dates go to end
      
      // Check if events are expired
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const eventDateA = new Date(dateA);
      eventDateA.setHours(0, 0, 0, 0);
      const eventDateB = new Date(dateB);
      eventDateB.setHours(0, 0, 0, 0);
      
      const isExpiredA = eventDateA < today;
      const isExpiredB = eventDateB < today;
      
      // First, separate expired from non-expired (non-expired first)
      if (isExpiredA !== isExpiredB) {
        return isExpiredA ? 1 : -1; // Expired events go to bottom
      }
      
      // For same expiration status, sort by date (newer first = descending)
      const comparison = timeB - timeA; // Descending: newer dates first
      return comparison;
    });
    
    // Debug: Log sorted dates to verify
    console.log(`📅 Events sorted by date (${sortDirection}):`, sorted.map((e, idx) => {
      const parsedDate = new Date(e.date);
      return {
        index: idx,
        title: e.title?.substring(0, 30),
        dateString: e.date,
        dateISO: parsedDate.toISOString(),
        timestamp: parsedDate.getTime()
      };
    }));
    
    return sorted;
  }, [events, filter, sortDirection]);

  const getStatusBadgeClass = (status: string) => {
    const baseClass = 'status-badge';
    switch (status) {
      case 'published':
        return `${baseClass} status-published`;
      case 'draft':
        return `${baseClass} status-draft`;
      case 'pending_review':
        return `${baseClass} status-pending`;
      case 'cancelled':
        return `${baseClass} status-cancelled`;
      default:
        return baseClass;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isPastEvent = (dateString: string) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-container">
          <div className="header-content">
            <h1>Cultural Events Dashboard</h1>
            <div className="header-actions">
              <button 
                className="btn-upload"
                onClick={() => navigate('/admin/cultural-events/upload')}
              >
                + Upload Event
              </button>
              <button className="btn-logout" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="admin-stats">
        <div className="admin-container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Events</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.published}</div>
              <div className="stat-label">Published</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.drafts}</div>
              <div className="stat-label">Drafts</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.pending}</div>
              <div className="stat-label">Pending Review</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="admin-content">
        <div className="admin-container">
          {/* Filters */}
          <div className="content-filters">
            <div className="filter-group">
              <button 
                className={filter === 'all' ? 'filter-btn active' : 'filter-btn'}
                onClick={() => setFilter('all')}
              >
                All ({stats.total})
              </button>
              <button 
                className={filter === 'published' ? 'filter-btn active' : 'filter-btn'}
                onClick={() => setFilter('published')}
              >
                Published ({stats.published})
              </button>
              <button 
                className={filter === 'draft' ? 'filter-btn active' : 'filter-btn'}
                onClick={() => setFilter('draft')}
              >
                Drafts ({stats.drafts})
              </button>
              <button 
                className={filter === 'pending_review' ? 'filter-btn active' : 'filter-btn'}
                onClick={() => setFilter('pending_review')}
              >
                Pending ({stats.pending})
              </button>
            </div>
            <button 
              className="sort-btn"
              onClick={toggleSortDirection}
              title={`Sort by date ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
            >
              📅 Date {sortDirection === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          {/* Events Table */}
          {loading ? (
            <div className="content-loading">
              <LoadingSpinner />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="content-empty">
              <p>No events found</p>
              <button 
                className="btn-upload"
                onClick={() => navigate('/admin/cultural-events/upload')}
              >
                Upload Your First Event
              </button>
            </div>
          ) : (
            <div className="events-table">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map(event => (
                    <tr key={event._id}>
                      <td data-label="Title">
                        <div className="event-title-cell">
                          {event.title}
                        </div>
                      </td>
                      <td data-label="Date">{formatDate(event.date)}</td>
                      <td data-label="Type" style={{ textTransform: 'capitalize' }}>{event.eventType}</td>
                      <td data-label="Status">
                        <span className={getStatusBadgeClass(event.status)}>
                          {event.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td data-label="Price">{event.isFree ? 'Free' : `£${event.price}`}</td>
                      <td data-label="Actions">
                        {isPastEvent(event.date) ? (
                          <span style={{ color: '#DC3545', fontWeight: 600 }}>Expired</span>
                        ) : (
                          <div className="action-buttons">
                            {event.status === 'published' ? (
                              <button 
                                className="btn-action btn-unpublish"
                                onClick={() => handleUnpublish(event._id)}
                              >
                                Unpublish
                              </button>
                            ) : (
                              <button 
                                className="btn-action btn-publish"
                                onClick={() => handlePublish(event._id)}
                              >
                                Publish
                              </button>
                            )}
                            <button 
                              className="btn-action btn-view"
                              onClick={() => window.open(`/cultural-events/${event._id}`, '_blank')}
                            >
                              View
                            </button>
                            <button 
                              className="btn-action btn-delete"
                              onClick={() => handleDelete(event._id)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

